import type { ReactNode } from 'react';
import { formatCount } from '@/shared/format';
import type { AiUsageSummary } from '@/api/schemas/ai';

/**
 * The tenant's usage for the current period.
 *
 * Split by WHO PAYS, not by how the call was made: platform-funded usage is
 * the platform's allowance being spent, tenant-owned usage is the tenant's
 * own provider being used. A tenant reading "412 requests" cannot tell
 * whether that was their own money; this split makes it obvious.
 */
export type UsageOverviewProps = {
  usage: AiUsageSummary;
};

export function UsageOverview({ usage }: UsageOverviewProps): ReactNode {
  return (
    <div className="space-y-6">
      <section className="space-y-3 rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium">Usage this period</h3>
        <p className="text-sm text-content-muted">Period {usage.periodKey}</p>

        <div className="grid gap-4 sm:grid-cols-2">
          <UsageCard
            title="Platform-funded"
            description="Paid for by the platform's allowance."
            requests={usage.platformRequestCount}
            tokens={usage.platformTokenCount}
          />
          <UsageCard
            title="Tenant-owned"
            description="Paid for by your own provider."
            requests={usage.tenantOwnedRequestCount}
            tokens={usage.tenantOwnedTokenCount}
          />
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium">Allowances</h3>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Tenant requests" value={limitLabel(usage.limits.tenantRequestLimit)} />
          <Field label="Tenant tokens" value={limitLabel(usage.limits.tenantTokenLimit)} />
          <Field label="Platform requests" value={limitLabel(usage.limits.platformRequestLimit)} />
          <Field label="Platform tokens" value={limitLabel(usage.limits.platformTokenLimit)} />
        </dl>
      </section>
    </div>
  );
}

function UsageCard({
  title,
  description,
  requests,
  tokens,
}: {
  title: string;
  description: string;
  requests: number;
  tokens: number | null;
}): ReactNode {
  return (
    <div className="space-y-2 rounded-lg border border-border p-4">
      <p className="font-medium text-content">{title}</p>
      <p className="text-xs text-content-muted">{description}</p>
      <dl className="grid grid-cols-2 gap-3 pt-1">
        <div>
          <dt className="text-xs tracking-wide text-content-muted uppercase">Requests</dt>
          <dd className="text-sm text-content">{formatCount(requests)}</dd>
        </div>
        <div>
          <dt className="text-xs tracking-wide text-content-muted uppercase">Tokens</dt>
          <dd className="text-sm text-content">
            {tokens === null ? 'Not reported' : formatCount(tokens)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function limitLabel(limit: number | null): string {
  return limit === null ? 'No limit' : formatCount(limit);
}

function Field({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-content-muted uppercase">{label}</dt>
      <dd className="text-sm text-content">{value}</dd>
    </div>
  );
}
